import React, { useState } from 'react';
import { useFloating, offset, flip, shift } from '@floating-ui/react';
import { SummaryLevel } from '../types';

interface SummaryLevelSelectorProps {
  value: SummaryLevel['type'];
  onChange: (level: SummaryLevel['type']) => void;
  className?: string;
  translations?: {
    ultra_short: string;
    ultra_short_desc: string;
    points_cles: string;
    points_cles_desc: string;
    detailed: string;
    detailed_desc: string;
    maxCharacters: string;
    characters: string;
    tip: {
      title: string;
      content: string;
    };
  };
}

const SummaryLevelSelector: React.FC<SummaryLevelSelectorProps> = ({
  value,
  onChange,
  className = '',
  translations,
}) => {
  const levels = [
    {
      type: 'ultra-court' as const,
      label: translations?.ultra_short || 'Ultra-court',
      description: translations?.ultra_short_desc || 'Une phrase ou deux',
      explanation: 'Idéal pour un aperçu rapide ou un titre accrocheur',
      maxLength: 200,
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
      ),
    },
    {
      type: 'bullet-points' as const,
      label: translations?.points_cles || 'Points clés',
      description: translations?.points_cles_desc || 'Liste à puces',
      explanation:
        'Parfait pour capturer les informations essentielles de manière structurée',
      maxLength: 500,
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
          />
        </svg>
      ),
    },
    {
      type: 'detaillé' as const,
      label: translations?.detailed || 'Détaillé',
      description: translations?.detailed_desc || 'Résumé complet',
      explanation: 'Résumé approfondi avec contexte et détails importants',
      maxLength: 1000,
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
    },
  ];

  return (
    <div className={`space-y-3 sm:space-y-4 ${className}`}>
      <label className="block text-sm font-semibold text-gray-800 dark:text-gray-100">
        Niveau de résumé
      </label>
      <div className="flex gap-2 sm:gap-3">
        {levels.map((level) => (
          <LevelButton
            key={level.type}
            level={level}
            isSelected={value === level.type}
            onSelect={() => onChange(level.type)}
            translations={translations}
          />
        ))}
      </div>

      {/* Explanation below */}
      <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-start space-x-3">
          <svg
            className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 max-sm:text-justify">
              <span className="font-medium">
                {translations?.tip?.title || 'Conseil'} :
              </span>{' '}
              {translations?.tip?.content ||
                'Choisissez le niveau selon votre besoin.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant séparé pour chaque bouton avec tooltip Floating UI
interface LevelButtonProps {
  level: {
    type: string;
    label: string;
    description: string;
    explanation: string;
    maxLength: number;
    icon: React.ReactNode;
  };
  isSelected: boolean;
  onSelect: () => void;
  translations?: {
    maxCharacters: string;
    characters: string;
  };
}

const LevelButton: React.FC<LevelButtonProps> = ({
  level,
  isSelected,
  onSelect,
  translations,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles } = useFloating({
    placement: 'top',
    middleware: [offset(8), flip(), shift()],
    open: isOpen,
    onOpenChange: setIsOpen,
  });

  // Temporairement désactivé pour éviter les erreurs de compilation
  // const hover = useHover({
  //   delay: { open: 200, close: 0 },
  // });

  // const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  return (
    <div className="relative flex-1">
      <button
        ref={refs.setReference}
        type="button"
        onClick={onSelect}
        className={`w-full p-2 sm:p-3 lg:p-6 text-left border-2 rounded-lg sm:rounded-xl transition-all duration-200 ${
          isSelected
            ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/40 dark:to-blue-800/40 text-blue-800 dark:text-blue-100 shadow-lg'
            : 'border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-md'
        }`}
      >
        {/* Icon and Header */}
        <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-3 mb-1 sm:mb-2 lg:mb-3">
          <div
            className={`p-1 sm:p-1.5 lg:p-2 rounded-lg transition-colors flex-shrink-0 ${
              isSelected
                ? 'bg-blue-200 dark:bg-blue-700/50 text-blue-700 dark:text-blue-200'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
            }`}
          >
            <div className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6">
              {level.icon}
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div
              className={`font-semibold text-xs sm:text-sm lg:text-lg truncate ${
                isSelected
                  ? 'text-blue-800 dark:text-blue-500'
                  : 'text-gray-900 dark:text-gray-100'
              }`}
            >
              {level.label}
            </div>
            <div
              className={`text-xs truncate hidden sm:block ${
                isSelected
                  ? 'text-blue-600 dark:text-blue-200'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {level.description}
            </div>
          </div>
        </div>

        {/* Character Limit */}
        <div
          className={`text-xs font-medium ${
            isSelected
              ? 'text-blue-500 dark:text-blue-300'
              : 'text-gray-400 dark:text-gray-500'
          }`}
        >
          <span className="sm:hidden">
            {level.maxLength}{' '}
            {translations?.characters?.slice(0, 10) || 'caractères'}
          </span>
          <span className="hidden sm:inline">
            {translations?.maxCharacters || 'Max'} {level.maxLength}{' '}
            {translations?.characters || 'caractères'}
          </span>
        </div>
      </button>

      {/* Tooltip avec Floating UI */}
      {isOpen && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className="z-50 w-56 sm:w-64 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-xl"
        >
          <div className="text-center">
            <div className="font-semibold mb-1 text-xs sm:text-sm">
              {level.label}
            </div>
            <div className="text-gray-300 text-xs">{level.explanation}</div>
          </div>
          {/* Arrow */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

export default SummaryLevelSelector;
